import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import { toast } from "react-toastify";
import { z } from "zod";
import { signOut } from "firebase/auth";
import { auth } from "lib/firebase/firebase";
import { AuthContext } from "provider/AuthProvider";
import { constants } from "lib/utilities/Auth";
import { validateDomain } from "lib/utilities/urlUtilities";
import { fetchAccessiblePages } from "lib/utilities/fetchAccesiblePages";
import { BASE_URL, techyrrInviteId, techyrrCompanyId } from "lib/utilities/globalConstants";
import Loader from "customComponents/Loader";
// import Imagefile from 'assets/images/login.png';
import  CircleMatrix  from "examples/CircleMatrix" 

function SignInForm({ techyrrAdmin }) {
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });

  const { login, getCustomTokenFromBackend, user } = useContext(AuthContext);
  const { errorCodes } = constants;
  const [queryParameters] = useSearchParams();
  const navigate = useNavigate();
  const { origin } = useParams();

  const onChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(6, "Invalid Password"),
  });

  const handleSignin = async (e) => {
    e.preventDefault();
    setSignInLoading(true);
    const validationResult = loginSchema.safeParse(credentials);

    if (!validationResult.success) {
      setSignInLoading(false);
      toast.error(<p>{validationResult.error.errors[0].message}</p>);
      return;
    }

    try {
      const res = await login(credentials.email, credentials.password);
      const tokenResult = await res.user.getIdTokenResult();

      await fetchAccessiblePages(res.user);

      if (techyrrAdmin && !tokenResult.claims.techyrrAdmin) {
        const response = await fetch(`${BASE_URL}admin/signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            inviteId: techyrrInviteId,
            email: credentials.email,
            password: credentials.password,
            phone: tokenResult.claims.phone,
            gender: tokenResult.claims.gender,
            displayName: res.user.displayName,
            companyId: techyrrCompanyId,
            techyrrAdmin: true,
          }),
        });
        if (!response.ok) throw new Error("Failed to assign Techyrr Admin");
      }

      if (!techyrrAdmin && tokenResult.claims.admin) {
        navigate("/admin/dashboard");
      }

      setLoading(true);
      setCredentials({ email: "", password: "" });

      const referrer = document.referrer;
      if (referrer && validateDomain(referrer)) {
        const customToken = await getCustomTokenFromBackend(res.user.uid);
        window.parent.postMessage({ token: customToken }, "*");
      }

      if (techyrrAdmin) {
        navigate("/techyrr/take-test", { state: { openResumeModal: true } });
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      toast.error(
        <p>{errorCodes.firebase[error.code] || error.message || "Try Again"}</p>
      );
    } finally {
      if (!origin) setLoading(false);
      setSignInLoading(false);
    }
  };

  useEffect(() => {
    if (queryParameters.get("signout") === "true") {
      signOut(auth);
    } else {
      const referrer = document.referrer;
      if (referrer && validateDomain(referrer) && user) {
        setLoading(true);
        getCustomTokenFromBackend(user.uid)
          .then((token) => window.parent.postMessage({ token }, "*"))
          .finally(() => {
            if (!origin) setLoading(false);
          });
      }
    }
  }, [user]);

  const goToSignUp = () => {
    if (origin) navigate(`/signup/${origin}`);
    else if (techyrrAdmin) navigate("/techyrr/signup");
    else navigate("/signup");
  };

  const goToForgotPassword = () => {
    if (origin) navigate(`/forgot-password/${origin}`);
    else if (techyrrAdmin) navigate("/techyrr/forgot-password");
    else navigate("/forgot-password");
  };

  return (
    <>
      {loading && <Loader />}
      <div className="flex min-h-screen w-full bg-gray-50">
        <div className="flex w-full items-center justify-center md:w-2/5 bg-white px-6 py-10">
          <div className="w-full max-w-md">
            <h1 className="text-3xl font-semibold text-gray-900 mb-2 text-center">
              {origin === "techyrr" ? (techyrrAdmin ? "Recruiter" : "Student") : ""} Sign In
            </h1>
            <p className="text-center text-gray-500 mb-6">Sign in to continue with Neusort</p>

            <form onSubmit={handleSignin} className="space-y-4">
              <div className="relative">
                <Email className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={credentials.email}
                  onChange={onChange}
                  className="w-full rounded-lg bg-gray-100 py-3 pl-10 pr-4 text-gray-900 focus:outline-none focus:bg-gray-200"
                  placeholder="Email"
                  required
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={onChange}
                  className="w-full rounded-lg bg-gray-100 py-3 pl-10 pr-10 text-gray-900 focus:outline-none focus:bg-gray-200"
                  placeholder="Password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <VisibilityOff className="h-5 w-5" /> : <Visibility className="h-5 w-5" />}
                </button>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    className="accent-black"
                  />
                  <span>Remember me</span>
                </label>
                <button type="button" onClick={goToForgotPassword} className="hover:underline">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={signInLoading}
                className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors"
              >
                {signInLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-600">
              Don&apos;t have an account?{" "}
              <button onClick={goToSignUp} className="text-black underline font-medium">
                Sign up
              </button>
            </p>
          </div>
        </div>

        <div className="relative hidden md:block md:w-3/5">
        <CircleMatrix />
      </div>
      </div>
    </>
  );
}

export default SignInForm;
